import { useEffect, useState } from 'react';

export interface UseMediaQueryReturn {
  isMobile: boolean;
  isMobileLarge: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

// Function overloads for proper type inference
export function useMediaQuery(query: string): boolean;
export function useMediaQuery(): UseMediaQueryReturn;
export function useMediaQuery(query?: string): UseMediaQueryReturn | boolean {
  // If query provided, use simple boolean mode
  if (query) {
    const [matches, setMatches] = useState<boolean>(false);

    useEffect(() => {
      const mediaQuery = window.matchMedia(query);

      setMatches(mediaQuery.matches);

      const handler = (event: MediaQueryListEvent) => {
        setMatches(event.matches);
      };

      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handler);

        return () => mediaQuery.removeEventListener('change', handler);
      } else {
        mediaQuery.addListener(handler);

        return () => mediaQuery.removeListener(handler);
      }
    }, [query]);

    return matches;
  }

  // Advanced mode - return object with multiple breakpoints
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileLarge, setIsMobileLarge] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 640px)');
    const mobileLargeQuery = window.matchMedia(
      '(min-width: 641px) and (max-width: 768px)',
    );
    const tabletQuery = window.matchMedia(
      '(min-width: 769px) and (max-width: 1024px)',
    );
    const desktopQuery = window.matchMedia('(min-width: 1025px)');

    const updateMatches = () => {
      setIsMobile(mobileQuery.matches);
      setIsMobileLarge(mobileLargeQuery.matches);
      setIsTablet(tabletQuery.matches);
      setIsDesktop(desktopQuery.matches);
    };

    updateMatches();

    const queries = [mobileQuery, mobileLargeQuery, tabletQuery, desktopQuery];

    for (const q of queries) {
      if (q.addEventListener) {
        q.addEventListener('change', updateMatches);
      } else {
        q.addListener(updateMatches);
      }
    }

    return () => {
      for (const q of queries) {
        if (q.removeEventListener) {
          q.removeEventListener('change', updateMatches);
        } else {
          q.removeListener(updateMatches);
        }
      }
    };
  }, []);

  return { isDesktop, isMobile, isMobileLarge, isTablet };
}
